// src/services/queries/mediaInsights/youtube.ts
// YouTube API types and fetch functions

import type { QueryFunction } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services/";
import { queryKeys } from "@/constants/queryKeys";
import { TIME } from "@/constants/app";

// =============================================================================
// Core Types
// =============================================================================

export interface YouTubeSection {
  id?: string;
  video_id: string;
  section_index: number;
  title: string;
  content: string;
  alpha_score?: number;
  alpha_scores?: Record<string, number>;
  alpha_reasoning?: string;
  sentiment?: "bullish" | "bearish" | "neutral";
  sentiment_score?: number;
  categories?: string[];
  urgency?: "immediate" | "this_week" | "this_month" | "background";
  created_at?: string;
}

export interface YouTubeTicker {
  id?: string;
  video_id: string;
  ticker: string;
  ticker_name?: string;
  main_points: string;
  sentiment?: "bullish" | "bearish" | "neutral";
  sentiment_score?: number;
  sentiment_reasoning?: string;
  price_levels?: {
    support?: number[];
    resistance?: number[];
    targets?: number[];
  };
  time_horizon?: "immediate" | "short_term" | "medium_term" | "long_term" | "unspecified";
  created_at?: string;
}

export interface YouTubeVideoBase {
  id?: string;
  video_id: string;
  title: string;
  description?: string;
  channel_id: string;
  channel_name?: string;
  upload_date?: string;
  published_at?: string;
  thumbnail_default?: string;
  thumbnail_medium?: string;
  thumbnail_high?: string;
  thumbnail_maxres?: string;
  duration?: string;
  duration_seconds?: number;
  view_count?: number;
  like_count?: number;
  comment_count?: number;
  raw_transcript?: string;
  transcript_language?: string;
  embedding_model?: string;
  categories?: string[];
  overall_sentiment?: "bullish" | "bearish" | "neutral";
  overall_sentiment_score?: number;
  processing_status?: "pending" | "processing" | "completed" | "failed";
  processing_error?: string;
  ai_model?: string;
  created_at?: string;
  updated_at?: string;
  processed_at?: string;
}

export interface YouTubeVideo extends YouTubeVideoBase {
  sections?: YouTubeSection[];
  tickers?: YouTubeTicker[];
}

export interface YouTubeVideoSummary {
  video_id: string;
  title: string;
  channel_id: string;
  channel_name?: string;
  upload_date?: string;
  published_at?: string;
  thumbnail_maxres?: string;
  duration?: string;
  duration_seconds?: number;
  categories?: string[];
  overall_sentiment?: "bullish" | "bearish" | "neutral";
  overall_sentiment_score?: number;
  processing_status?: string;
  created_at?: string;
  // Aggregated data
  tickers?: string[];
  section_alpha_scores?: number[];
}

// Helper function to calculate video alpha score from section scores
// Takes average of top 2 (or 1 if only 1 available)
export const calculateVideoAlpha = (sectionAlphaScores?: number[]): number | null => {
  if (!sectionAlphaScores || sectionAlphaScores.length === 0) return null;
  const sorted = [...sectionAlphaScores].sort((a, b) => b - a);
  const topScores = sorted.slice(0, Math.min(2, sorted.length));
  return topScores.reduce((sum, s) => sum + s, 0) / topScores.length;
};

// =============================================================================
// API Response Types
// =============================================================================

export interface YouTubeVideoDetailResponse {
  data: YouTubeVideo;
}

export interface YouTubeVideoListResponse {
  data: YouTubeVideoSummary[];
  total?: number;
}

// =============================================================================
// Fetch Functions
// =============================================================================

/**
 * Fetch all videos for home page (single API call)
 */
export const fetchAllVideos: QueryFunction<YouTubeVideoListResponse> = async ({
  signal,
}) => {
  const url = `${API_BASE_URL}/multimediaInsightPage/getAllVideos`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    ...(signal && { signal }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch all videos (${response.status}): ${text || response.statusText}`);
  }

  return response.json();
};

/**
 * Fetch full video details with sections and tickers
 */
export const fetchVideoDetail: QueryFunction<YouTubeVideoDetailResponse> = async ({
  queryKey,
  signal,
}) => {
  const [, , , videoId, includeSections, includeTickers] = queryKey as [string, string, string, string, boolean, boolean];

  if (!videoId) {
    throw new Error("videoId is required");
  }

  const params = new URLSearchParams();
  if (includeSections !== undefined) params.set("include_sections", String(includeSections));
  if (includeTickers !== undefined) params.set("include_tickers", String(includeTickers));

  const url = `${API_BASE_URL}/multimediaInsightPage/videos/${videoId}${params.toString() ? `?${params}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Video detail request failed (${response.status}): ${text || response.statusText}`);
  }

  return response.json();
};

/**
 * Fetch all videos for a channel (for video page sidebar)
 */
export const fetchChannelVideos: QueryFunction<YouTubeVideoListResponse> = async ({
  queryKey,
  signal,
}) => {
  const [, , , channelId, limit, offset] = queryKey as [string, string, string, string, number, number];

  if (!channelId) {
    throw new Error("channelId is required");
  }

  const params = new URLSearchParams();
  if (limit !== undefined) params.set("limit", String(limit));
  if (offset !== undefined) params.set("offset", String(offset));

  const url = `${API_BASE_URL}/multimediaInsightPage/channels/${channelId}/videos${params.toString() ? `?${params}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Channel videos request failed (${response.status}): ${text || response.statusText}`);
  }

  return response.json();
};

// =============================================================================
// Query Options
// =============================================================================

export const allVideosQueryOptions = () => ({
  queryKey: queryKeys.mediaInsights.youtube.allVideos(),
  queryFn: fetchAllVideos,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 1 * TIME.HOUR,
  retry: (failureCount: number, error: Error) => {
    if (error.message.includes("404")) return false;
    return failureCount < 3;
  },
  refetchOnWindowFocus: false,
});

export const videoDetailQueryOptions = (
  videoId: string, 
  includeSections = true, 
  includeTickers = true
) => ({
  queryKey: queryKeys.mediaInsights.youtube.videoDetail(videoId, includeSections, includeTickers),
  queryFn: fetchVideoDetail,
  staleTime: 24 * TIME.HOUR,
  gcTime: 48 * TIME.HOUR,
  retry: (failureCount: number, error: Error) => {
    if (error.message.includes("404")) return false;
    return failureCount < 3;
  },
  refetchOnWindowFocus: false,
  enabled: !!videoId && videoId.trim().length > 0,
});

export const channelVideosQueryOptions = (channelId: string, limit = 50, offset = 0) => ({
  queryKey: queryKeys.mediaInsights.youtube.channelVideos(channelId, limit, offset),
  queryFn: fetchChannelVideos,
  staleTime: 1 * TIME.HOUR,
  gcTime: 24 * TIME.HOUR,
  retry: (failureCount: number, error: Error) => {
    if (error.message.includes("404")) return false;
    return failureCount < 3;
  },
  refetchOnWindowFocus: false,
  enabled: !!channelId && channelId.trim().length > 0,
});
